import { DraftConverter, mapBlockToNode, blockToNodeMapping } from '@tiptap/draftjs-to-tiptap';
import type { RawDraftContentBlock, RawDraftContentState } from 'draft-js';
import { isEmpty } from 'lodash/fp';

import { TextAlignment } from '../../../../table/CellTypeConstants';
import { ALIGNMENT_SUPPORTED_NODE_TYPES, TiptapContentNode, TiptapNodeType } from '../../../tiptapTypes';
import convertChecklistBlockToTiptapNode from './convertChecklistBlockToTiptapNode';
import convertDraftInlineStyleToTiptapMark from './convertDraftInlineStyleToTiptapMark';
import { convertEntityToMark } from './convertEntityToMark';
import { DraftConverterSpec, MapBlockToNodeFn } from './draftToTiptapConverterTypes';
import { postprocessContent } from './postprocessing';
import { rewriteTree } from './rewriteTree';

const convertSmallTextBlockToNode: MapBlockToNodeFn = (context) => {
    const { doc, converter, block, entityMap } = context;

    return converter.addChild(
        converter.createNode('smallText'),
        converter.splitTextByEntityRangesAndInlineStyleRanges({
            doc,
            block,
            entityMap,
        }),
    );
};

/**
 * This is the OOTB @tiptap/draftjs-to-tiptap converter's implementation
 * of the "unstyled" block type, except for the way that it handles
 * blocks with no text.
 *
 * The original implementation would return null in this case. This would
 * result in the block being skipped entirely, which is not what we want.
 *
 * Instead, we return an empty paragraph node - this matches the Tiptap behaviour
 * when creating a Tiptap card from scratch with empty lines.
 */
const convertUnstyledBlockToNode: MapBlockToNodeFn = (context) => {
    const { converter, doc, block, entityMap } = context;

    const paragraph = converter.createNode(TiptapNodeType.paragraph);

    if (!block.text) return paragraph;

    if (block.inlineStyleRanges.length === 0) {
        if (block.entityRanges.length === 0) {
            // Plain text, fast path
            return converter.addChild(paragraph, converter.createText(block.text));
        }
    }

    return converter.addChild(
        paragraph,
        converter.splitTextByEntityRangesAndInlineStyleRanges({
            doc,
            block,
            entityMap,
        }),
    );
};

const convertCodeBlockToNode: MapBlockToNodeFn = (context) => {
    const { converter, doc, block, entityMap } = context;

    const codeBlock = converter.createNode(TiptapNodeType.codeBlock);
    codeBlock.attrs = {
        language: block.data?.language ?? null,
    };
    if (!block.text) return codeBlock;

    return converter.addChild(
        codeBlock,
        converter.splitTextByEntityRangesAndInlineStyleRanges({
            doc,
            block,
            entityMap,
        }),
    );
};

/**
 * For other block types we can use the default converter.
 *
 * We've encountered some instances where the block-type is not an expected type (see WEB-12837),
 * e.g. "article".
 *
 * Previously these blocks would get removed entirely, resulting in content loss for the user.
 * Instead, we will now convert them to a normal unstyled block.
 */
const convertStandardBlockToNode: MapBlockToNodeFn = (context) => {
    // use tiptap's default converter
    const result = mapBlockToNode(context);

    if (result) return result;

    try {
        return blockToNodeMapping.unstyled(context);
    } catch (error) {
        // The block type and the error only. The block itself holds the user's text, and this
        // also runs server-side over every task list in the corpus.
        console.error(`Failed to convert a "${context.block.type}" block when falling back to unstyled`, error);
    }

    return null;
};

const convertBlockToNode: MapBlockToNodeFn = (context) => {
    switch (context.block.type) {
        case 'small-text':
            return convertSmallTextBlockToNode(context);
        case 'checklist':
            return convertChecklistBlockToTiptapNode(context);
        case 'unstyled':
            return convertUnstyledBlockToNode(context);
        case 'code-block':
            return convertCodeBlockToNode(context);
        default:
            return convertStandardBlockToNode(context);
    }
};

const getAlignment = (node: TiptapContentNode, block: RawDraftContentBlock): TextAlignment | null => {
    if (!ALIGNMENT_SUPPORTED_NODE_TYPES.includes((node.type as TiptapNodeType) || '')) return null;

    if (block.data?.['text-align-center']) return TextAlignment.CENTER;
    if (block.data?.['text-align-right']) return TextAlignment.RIGHT;
    return TextAlignment.LEFT;
};

const addGlobalNodeAttrs = (node: TiptapContentNode, block: RawDraftContentBlock) => {
    let newAttrs: Record<string, any> = {
        textAlign: getAlignment(node, block),
    };

    newAttrs = Object.fromEntries(Object.entries(newAttrs).filter(([_, v]) => v !== null));

    if (!isEmpty(newAttrs)) {
        return {
            ...node.attrs,
            ...newAttrs,
        };
    }

    return node.attrs;
};

const converter = new DraftConverter({
    mapBlockToNode(context) {
        const node = convertBlockToNode(context);
        if (!node) return node;

        // Some draft blocks have empty content, which is invalid in Tiptap -- just omit
        if (node.type === 'text' && !node.text) return null;

        const attrs = addGlobalNodeAttrs(node, context.block);
        if (attrs) {
            node.attrs = attrs;
        }

        return node;
    },
    mapEntityToMark(context) {
        // The mapEntityToMark function exported by draftjs-to-tiptap is hardcoded to only handle
        // links, and strips the data from all other mark types, making it impossible to convert
        // them. This does roughly the same thing, but keeps the data for non-link entities.
        const entity = context.entityMap[context.range.key];
        const mark = convertEntityToMark(entity, String(context.range.key));

        if (!mark) return null;

        return converter.addMark(mark);
    },
    mapInlineStyleToMark(context) {
        return convertDraftInlineStyleToTiptapMark(context);
    },
} as DraftConverterSpec);

/**
 * Converts an element's textContent (exported so tests don't have to set up a whole MNElement)
 */
export const convertDraftContentToTiptap = (textContent: RawDraftContentState): TiptapContentNode => {
    const baseConversion = converter.convert(textContent);

    return rewriteTree(baseConversion, postprocessContent);
};
